import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Star, Flame, Sparkles, ArrowRight, Trophy, ChevronDown, Award } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../content';
import type { ModuleId } from '../../content';
import { getModuleIcon, getFamiliesForModule, getSkillsForModule, getFamiliesForModuleSkill, getFamilyDefinition } from '../../content';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection } from '../../mascot';
import { Card } from '../ui/Card';
import { ErrorPatternsSection } from './ErrorPatternsSection';
import { MilestonesSection } from './MilestonesSection';
import { PersonalRecordsSection } from './PersonalRecordsSection';
import { PageHero, HeroStatPill } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { WeeklyActivityDots } from '../shared/WeeklyActivityDots';
import { getWeeklyActivityForLearner } from '../../utils/activityHelpers';
import { getGrowthStage, getFamilyMasteryKey } from '../../mastery';
import { GrowthStageIcon } from '../shared/GrowthStageIcon';
import { EmptyState } from '../shared/EmptyState';
import type { AssessmentReport } from '../../assessment';
const MODULE_COLORS: Record<ModuleId, {
  border: string;
  fill: string;
  iconBg: string;
  iconText: string;
  gradientFrom: string;
  gradientTo: string;
}> = {
  Metriek: {
    border: 'border-blue-200',
    fill: 'bg-blue-500',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100/30'
  },
  Getalbegrip: {
    border: 'border-indigo-200',
    fill: 'bg-indigo-500',
    iconBg: 'bg-indigo-100',
    iconText: 'text-indigo-600',
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100/30'
  },
  Basisrekenen: {
    border: 'border-orange-200',
    fill: 'bg-orange-500',
    iconBg: 'bg-orange-100',
    iconText: 'text-orange-600',
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100/30'
  },
  Tijd: {
    border: 'border-sky-200',
    fill: 'bg-sky-500',
    iconBg: 'bg-sky-100',
    iconText: 'text-sky-600',
    gradientFrom: 'from-sky-50',
    gradientTo: 'to-sky-100/30'
  },
  Geld: {
    border: 'border-yellow-200',
    fill: 'bg-yellow-500',
    iconBg: 'bg-yellow-100',
    iconText: 'text-yellow-600',
    gradientFrom: 'from-yellow-50',
    gradientTo: 'to-yellow-100/30'
  },
  SpellingWW: {
    border: 'border-fuchsia-200',
    fill: 'bg-fuchsia-500',
    iconBg: 'bg-fuchsia-100',
    iconText: 'text-fuchsia-600',
    gradientFrom: 'from-fuchsia-50',
    gradientTo: 'to-fuchsia-100/30'
  },
  SpellingRegel: {
    border: 'border-amber-200',
    fill: 'bg-amber-500',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    gradientFrom: 'from-amber-50',
    gradientTo: 'to-amber-100/30'
  },
  Taalbegrip: {
    border: 'border-purple-200',
    fill: 'bg-purple-500',
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-600',
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100/30'
  },
  Studievaardig: {
    border: 'border-teal-200',
    fill: 'bg-teal-500',
    iconBg: 'bg-teal-100',
    iconText: 'text-teal-600',
    gradientFrom: 'from-teal-50',
    gradientTo: 'to-teal-100/30'
  },
  Topo: {
    border: 'border-cyan-200',
    fill: 'bg-cyan-500',
    iconBg: 'bg-cyan-100',
    iconText: 'text-cyan-600',
    gradientFrom: 'from-cyan-50',
    gradientTo: 'to-cyan-100/30'
  }
};
const FALLBACK_COLORS = {
  border: 'border-slate-200',
  fill: 'bg-slate-500',
  iconBg: 'bg-slate-100',
  iconText: 'text-slate-600',
  gradientFrom: 'from-slate-50',
  gradientTo: 'to-slate-100/30'
};
interface ProgressPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onViewReport: (report: AssessmentReport) => void;
  onNavigate: (view: string) => void;
}
export function ProgressPage({
  state,
  selectedGroup,
  onViewReport,
  onNavigate
}: ProgressPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const currentStreak = state.dailyStreak?.currentWeekDays || 0;
  const records = useMemo(() => {
    let fastestTime = Infinity;
    let totalCorrect = 0;
    let totalAttempts = 0;
    if (state.questionStates) {
      Object.values(state.questionStates).forEach(qs => {
        qs.history?.forEach(attempt => {
          totalAttempts++;
          if (attempt.isCorrect) {
            totalCorrect++;
            if (attempt.responseTimeMs && attempt.responseTimeMs > 500) {
              fastestTime = Math.min(fastestTime, attempt.responseTimeMs);
            }
          }
        });
      });
    }
    return {
      fastestTime: fastestTime === Infinity ? null : fastestTime,
      accuracy: totalAttempts > 0 ? Math.round(totalCorrect / totalAttempts * 100) : 0,
      longestStreak: state.dailyStreak?.longestWeekStreak || 0,
      perfectSessions: state.dailyStreak?.totalPerfectSessions || 0,
      dailyGoals: state.dailyStreak?.totalDailyGoalsReached || 0
    };
  }, [state.questionStates, state.dailyStreak]);
  const weeklyActivity = useMemo(() => getWeeklyActivityForLearner(state.questionStates || {}), [state.questionStates]);
  const {
    totalAnswered,
    masteredCount,
    overallMastery
  } = useMemo(() => {
    let total = 0,
      mastered = 0,
      masterySum = 0;
    MODULES.forEach(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      total += getAnsweredQuestions(m.id).length;
      masterySum += mastery;
      if (mastery >= 85) mastered++;
    });
    return {
      totalAnswered: total,
      masteredCount: mastered,
      overallMastery: Math.round(masterySum / MODULES.length)
    };
  }, [getModuleMasteryForGroup, getAnsweredQuestions]);
  const topMasteredFamilies = useMemo(() => {
    if (!state.familyMastery) return [];
    const mastered: {
      familyId: string;
      score: number;
      def: any;
    }[] = [];
    for (const [key, data] of Object.entries(state.familyMastery)) {
      if (key.endsWith(`_g${selectedGroup}`) && data.masteryScore >= 70) {
        const def = getFamilyDefinition(data.familyId);
        if (def) mastered.push({
          familyId: data.familyId,
          score: data.masteryScore,
          def
        });
      }
    }
    return mastered.sort((a, b) => b.score - a.score).slice(0, 6);
  }, [state.familyMastery, selectedGroup]);
  // Modules with mastery data
  const activeModules = useMemo(() => {
    return MODULES.filter(module => {
      const families = getFamiliesForModule(module.id);
      return families.length > 0 && families.some(f => {
        const key = getFamilyMasteryKey(f.familyId, selectedGroup);
        return state.familyMastery?.[key] !== undefined;
      });
    });
  }, [state.familyMastery, selectedGroup]);
  const hasFamilyMastery = Object.keys(state.familyMastery || {}).length > 0;
  if (totalAnswered === 0 && !hasFamilyMastery) {
    return <PageContainer className="space-y-4 pb-20" data-id="element-1892">
        <motion.div variants={itemVariants} data-id="element-1893">
          <PageHero title="Mijn Voortgang" subtitle="Ontdek welke vaardigheden je al beheerst" gradientClasses="from-indigo-500 via-violet-500 to-purple-500" djayEmotion="happy" djayVariant={selectedVariant} icon={TrendingUp} iconBgClass="bg-indigo-400" floatingEmojis={['📊', '⭐']} data-id="element-1894" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-1895">
          <EmptyState preset="no-progress" title="Nog geen voortgang" message="Je hebt nog geen vragen beantwoord. Kies een wereld om te beginnen met oefenen!" actionLabel="Naar Mijn Werelden" onAction={() => onNavigate('modules')} emotion="encouraging" colorScheme="indigo" data-id="element-1896" />
        </motion.div>
      </PageContainer>;
  }
  return <PageContainer className="space-y-4 sm:space-y-5 pb-20" data-id="element-1897">
      {/* ── Hero ── */}
      <motion.div variants={itemVariants} data-id="element-1898">
        <PageHero title="Mijn Voortgang" subtitle="Ontdek welke vaardigheden je al beheerst" gradientClasses="from-indigo-500 via-violet-500 to-purple-500" djayEmotion="celebrating" djayVariant={selectedVariant} icon={TrendingUp} iconBgClass="bg-indigo-400" floatingEmojis={['📊', '⭐']} data-id="element-1899">
          <HeroStatPill icon={Star} data-id="element-1900">Level {state.stats.level}</HeroStatPill>
          <HeroStatPill icon={Sparkles} data-id="element-1901">
            {state.stats.xp.toLocaleString('nl-NL')} XP
          </HeroStatPill>
          <HeroStatPill icon={Flame} data-id="element-1902">{currentStreak} dagen streak</HeroStatPill>
        </PageHero>
      </motion.div>

      {/* ── Quick Stats + Weekly Activity ── */}
      <motion.div variants={itemVariants} data-id="element-1903">
        <Card className="p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl shadow-sm" data-id="element-1904">
          <div className="flex items-center gap-3 sm:gap-5" data-id="element-1905">
            <div className="flex items-center gap-2.5 flex-shrink-0" data-id="element-1906">
              <div className="relative w-12 h-12" data-id="element-1907">
                <svg className="w-12 h-12 transform -rotate-90" data-id="element-1908">
                  <circle cx="24" cy="24" r="20" fill="none" stroke="#F1F5F9" strokeWidth="4" data-id="element-1909" />
                  <circle cx="24" cy="24" r="20" fill="none" stroke="#6366F1" strokeWidth="4" strokeDasharray="125.7" strokeDashoffset={125.7 - overallMastery / 100 * 125.7} strokeLinecap="round" className="transition-all duration-1000 ease-out" data-id="element-1910" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-1911">
                  <span className="text-xs font-black text-slate-800" data-id="element-1912">
                    {overallMastery}%
                  </span>
                </div>
              </div>
              <div className="hidden sm:block" data-id="element-1913">
                <p className="text-sm font-bold text-slate-800" data-id="element-1914">
                  {masteredCount}/{MODULES.length} vakken
                </p>
                <p className="text-[11px] text-slate-500 font-medium" data-id="element-1915">
                  {totalAnswered.toLocaleString('nl-NL')} vragen
                </p>
              </div>
            </div>
            <div className="w-px h-10 bg-slate-200 flex-shrink-0" data-id="element-1916" />
            <div className="flex-1 min-w-0" data-id="element-1917">
              <WeeklyActivityDots weeklyActivity={weeklyActivity} color="indigo" data-id="element-1918" />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* ── Vaardigheden per Vak (MOVED UP — primary content) ── */}
      {activeModules.length > 0 && <motion.div variants={itemVariants} data-id="element-1919">
          <div className="space-y-2.5" data-id="element-1920">
            {activeModules.map(module => <ModuleProgressZone key={module.id} module={module} families={getFamiliesForModule(module.id)} state={state} selectedGroup={selectedGroup} data-id="element-1921" />)}
          </div>
        </motion.div>}

      {/* ── Sterkste Vaardigheden (compact horizontal scroll) ── */}
      {topMasteredFamilies.length > 0 && <motion.div variants={itemVariants} data-id="element-1922">
          <div className="space-y-2" data-id="element-1923">
            <div className="flex items-center gap-2 px-1" data-id="element-1924">
              <Award className="w-4 h-4 text-amber-500" data-id="element-1925" />
              <h3 className="text-sm font-bold text-slate-700" data-id="element-1926">
                Sterkste Vaardigheden
              </h3>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" data-id="element-1927">
              {topMasteredFamilies.map(({
            familyId,
            score,
            def
          }) => {
            const moduleDef = MODULES.find(m => m.id === def.module);
            const colors = moduleDef ? MODULE_COLORS[moduleDef.id as ModuleId] : FALLBACK_COLORS;
            const growthStage = getGrowthStage(score);
            return <div key={familyId} className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border ${colors.border} bg-white`} data-id="element-1928">
                    <span className="text-sm" data-id="element-1929">{growthStage.emoji}</span>
                    <div className="min-w-0" data-id="element-1930">
                      <p className="text-xs font-bold text-slate-800 truncate max-w-[140px]" data-id="element-1931">
                        {def.description || def.learningGoal}
                      </p>
                      <p className="text-[10px] text-slate-400 font-medium" data-id="element-1932">
                        {moduleDef?.title}
                      </p>
                    </div>
                    <div className="w-10 h-1.5 rounded-full bg-slate-100 overflow-hidden flex-shrink-0" data-id="element-1933">
                      <div className={`h-full ${colors.fill} rounded-full`} style={{
                  width: `${score}%`
                }} data-id="element-1934" />
                    </div>
                  </div>;
          })}
            </div>
          </div>
        </motion.div>}

      {/* ── Persoonlijke Records ── */}
      <motion.div variants={itemVariants} data-id="element-1935">
        <PersonalRecordsSection fastestTime={records.fastestTime} accuracy={records.accuracy} longestStreak={records.longestStreak} perfectSessions={records.perfectSessions} dailyGoals={records.dailyGoals} data-id="element-1936" />
      </motion.div>

      {/* ── Mijlpalen ── */}
      <motion.div variants={itemVariants} data-id="element-1937">
        <MilestonesSection state={state} selectedGroup={selectedGroup} data-id="element-1938" />
      </motion.div>

      {/* ── Slimme Leertips ── */}
      <motion.div variants={itemVariants} data-id="element-1939">
        <ErrorPatternsSection state={state} selectedGroup={selectedGroup} data-id="element-1940" />
      </motion.div>

      {/* ── Collectie Link ── */}
      <motion.div variants={itemVariants} data-id="element-1941">
        <button onClick={() => onNavigate('collection')} className="w-full text-left" data-id="element-1942">
          <Card className="p-3 sm:p-4 bg-amber-50 border-2 border-amber-200 rounded-2xl flex items-center gap-3 hover:shadow-md hover:border-amber-300 transition-all group shadow-sm" data-id="element-1943">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-1944">
              <Trophy className="w-4 h-4 text-amber-600" data-id="element-1945" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1946">
              <h3 className="font-bold text-slate-800 text-sm" data-id="element-1947">
                Mijn Collectie
              </h3>
              <p className="text-[11px] text-slate-500 font-medium" data-id="element-1948">
                Prestaties & Djay-varianten
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-1 transition-transform flex-shrink-0" data-id="element-1949" />
          </Card>
        </button>
      </motion.div>
    </PageContainer>;
}
// ── Module Progress Zone (compact) ────────────────────────────────────
function ModuleProgressZone({
  module,
  families,
  state,
  selectedGroup
}: {
  module: any;
  families: any[];
  state: AppState;
  selectedGroup: GroupId;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const colors = MODULE_COLORS[module.id as ModuleId] || FALLBACK_COLORS;
  const ModuleIcon = getModuleIcon(module.icon);
  const skills = getSkillsForModule(module.id);
  let totalScore = 0,
    count = 0;
  families.forEach(f => {
    const key = getFamilyMasteryKey(f.familyId, selectedGroup);
    const data = state.familyMastery?.[key];
    if (data) {
      totalScore += data.masteryScore;
      count++;
    }
  });
  const moduleMastery = count > 0 ? Math.round(totalScore / count) : 0;
  const growthStage = getGrowthStage(moduleMastery);
  return <div className={`rounded-2xl border-2 ${colors.border} bg-white shadow-sm overflow-hidden`} data-id="element-1950">
      <button onClick={() => setIsExpanded(!isExpanded)} className={`w-full px-3 py-3 sm:px-4 sm:py-3.5 flex items-center gap-3 bg-gradient-to-r ${colors.gradientFrom} ${colors.gradientTo} hover:opacity-90 transition-opacity`} data-id="element-1951">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.iconBg} flex-shrink-0`} data-id="element-1952">
          <ModuleIcon className={`w-4.5 h-4.5 ${colors.iconText}`} data-id="element-1953" />
        </div>
        <div className="flex-1 min-w-0 text-left" data-id="element-1954">
          <h3 className="text-sm font-black text-slate-900 leading-tight" data-id="element-1955">
            {module.title}
          </h3>
          <div className="flex items-center gap-2 mt-0.5" data-id="element-1956">
            <span className="text-[11px] font-medium text-slate-500" data-id="element-1957">
              {growthStage.emoji} {count} geoefend
            </span>
            <div className="w-16 h-1.5 bg-white/50 rounded-full overflow-hidden" data-id="element-1958">
              <div className={`h-full ${colors.fill} rounded-full`} style={{
              width: `${moduleMastery}%`
            }} data-id="element-1959" />
            </div>
          </div>
        </div>
        <div className={`w-6 h-6 rounded-full bg-white/80 flex items-center justify-center transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-1960">
          <ChevronDown className={`w-3.5 h-3.5 ${colors.iconText}`} data-id="element-1961" />
        </div>
      </button>

      <AnimatePresence data-id="element-1962">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="border-t border-slate-100" data-id="element-1963">
            <div className="p-3 sm:p-4 space-y-4" data-id="element-1964">
              {skills.map(skill => {
            const skillFamilies = getFamiliesForModuleSkill(module.id, skill);
            const activeFamilies = skillFamilies.filter(f => {
              const key = getFamilyMasteryKey(f.familyId, selectedGroup);
              return state.familyMastery?.[key] !== undefined;
            });
            if (activeFamilies.length === 0) return null;
            return <div key={skill} className="space-y-1.5" data-id="element-1965">
                    <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-1" data-id="element-1966">
                      {skill}
                    </h4>
                    <div className="space-y-1" data-id="element-1967">
                      {activeFamilies.map(f => {
                  const key = getFamilyMasteryKey(f.familyId, selectedGroup);
                  const data = state.familyMastery?.[key];
                  const score = data?.masteryScore || 0;
                  const gs = getGrowthStage(score);
                  return <div key={f.familyId} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-slate-50/80" data-id="element-1968">
                            <span className="text-xs flex-shrink-0" data-id="element-1969">
                              {gs.emoji}
                            </span>
                            <p className="text-xs font-medium text-slate-700 flex-1 min-w-0 truncate" data-id="element-1970">
                              {f.description || f.learningGoal}
                            </p>
                            <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden flex-shrink-0" data-id="element-1971">
                              <div className={`h-full ${colors.fill} rounded-full`} style={{
                        width: `${score}%`
                      }} data-id="element-1972" />
                            </div>
                          </div>;
                })}
                    </div>
                  </div>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}